const EMPTY_VALUE = '-';

const EDUCATION_ALIASES = {
  1: '1',
  2: '2',
  3: '3',
  4: '4',
  5: '5',
  incomplete_secondary: '1',
  secondary: '2',
  specialized_secondary: '3',
  secondary_special: '3',
  incomplete_higher: '4',
  higher: '5'
};

const FAMILY_STATUS_ALIASES = {
  1: '1',
  2: '2',
  single: '1',
  married: '2'
};

function isPresent(value) {
  if (value === null || value === undefined) {
    return false;
  }

  if (typeof value === 'string') {
    return value.trim() !== '';
  }

  return true;
}

function getOwnValue(source, keys) {
  if (!source || typeof source !== 'object') {
    return undefined;
  }

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }

    const value = source[key];

    if (value === null || value === undefined || value === '') {
      continue;
    }

    return value;
  }

  return undefined;
}

function getStringValue(source, keys) {
  const value = getOwnValue(source, keys);

  if (value === undefined || typeof value === 'object') {
    return null;
  }

  const normalized = String(value).trim();

  return normalized !== '' ? normalized : null;
}

function getBooleanValue(source, keys) {
  if (!source || typeof source !== 'object') {
    return null;
  }

  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }

    const value = source[key];

    if (value === null || value === undefined || value === '') {
      continue;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    if (typeof value === 'number') {
      return value !== 0;
    }

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();

      if (['t', 'true', '1', 'yes'].includes(normalized)) {
        return true;
      }

      if (['f', 'false', '0', 'no'].includes(normalized)) {
        return false;
      }
    }
  }

  return null;
}

function translateCode(translate, dictionaryPath, code) {
  if (!code) {
    return null;
  }

  const key = `${dictionaryPath}${code}`;
  const label = translate(key);

  if (!label || label === key) {
    return null;
  }

  return label;
}

function formatDictionaryValue(rawValue, aliases, translate, dictionaryPath) {
  if (!isPresent(rawValue) || typeof rawValue === 'object') {
    return EMPTY_VALUE;
  }

  const normalized = String(rawValue).trim();
  const code = aliases[normalized] || aliases[normalized.toLowerCase()] || normalized;
  const translated = translateCode(translate, dictionaryPath, code);

  return translated || normalized || EMPTY_VALUE;
}

function formatYesNo(value, translate, yesKey, noKey) {
  if (value === null) {
    return EMPTY_VALUE;
  }

  return value ? translate(yesKey) : translate(noKey);
}

export function getExtraUserInfoView(extraUserInfo, translate) {
  if (!extraUserInfo || typeof extraUserInfo !== 'object') {
    return {
      hasData: false,
      fields: []
    };
  }

  const education = getOwnValue(extraUserInfo, ['education']);
  const familyStatus = getOwnValue(extraUserInfo, ['family_status']);
  const driversLicense = getBooleanValue(extraUserInfo, ['drivers_license', 'driving_license']);
  const carOwner = getBooleanValue(extraUserInfo, ['car_owner', 'car_owned']);
  const realEstateOwner = getBooleanValue(extraUserInfo, ['real_estate_owner', 'real_estate_owned']);
  const childExpenses = getBooleanValue(extraUserInfo, ['child_expenses', 'need_to_refund_expenses']);
  const dependensNumber = getStringValue(extraUserInfo, ['dependens_number', 'number_of_dependents']);
  const totalIncome = getStringValue(extraUserInfo, ['total_income', 'monthly_income']);

  const fields = [
    {
      key: 'education',
      labelKey: 'message.changeWorkInfoPage.extraFields.education',
      value: formatDictionaryValue(
        education,
        EDUCATION_ALIASES,
        translate,
        'message.signUp.extraBlock.education.list.'
      )
    },
    {
      key: 'driversLicense',
      labelKey: 'message.changeWorkInfoPage.extraFields.driversLicense',
      value: formatYesNo(
        driversLicense,
        translate,
        'message.signUp.extraBlock.driversLicense.yes',
        'message.signUp.extraBlock.driversLicense.no'
      )
    },
    {
      key: 'carOwner',
      labelKey: 'message.changeWorkInfoPage.extraFields.carOwner',
      value: formatYesNo(
        carOwner,
        translate,
        'message.signUp.extraBlock.car.yes',
        'message.signUp.extraBlock.car.no'
      )
    },
    {
      key: 'realEstateOwner',
      labelKey: 'message.changeWorkInfoPage.extraFields.realEstateOwner',
      value: formatYesNo(
        realEstateOwner,
        translate,
        'message.signUp.extraBlock.realEstate.yes',
        'message.signUp.extraBlock.realEstate.no'
      )
    },
    {
      key: 'familyStatus',
      labelKey: 'message.changeWorkInfoPage.extraFields.familyStatus',
      value: formatDictionaryValue(
        familyStatus,
        FAMILY_STATUS_ALIASES,
        translate,
        'message.signUp.extraBlock.familyStatus.list.'
      )
    },
    {
      key: 'dependensNumber',
      labelKey: 'message.changeWorkInfoPage.extraFields.dependensNumber',
      value: dependensNumber || EMPTY_VALUE
    },
    {
      key: 'childExpenses',
      labelKey: 'message.changeWorkInfoPage.extraFields.childExpenses',
      value: formatYesNo(
        childExpenses,
        translate,
        'message.signUp.extraBlock.childExpenses.yes',
        'message.signUp.extraBlock.childExpenses.no'
      )
    },
    {
      key: 'totalIncome',
      labelKey: 'message.changeWorkInfoPage.extraFields.totalIncome',
      value: totalIncome || EMPTY_VALUE
    }
  ];

  return {
    hasData: true,
    fields
  };
}

const EXTRA_USER_INFO_EDUCATION_OPTIONS = ['1', '2', '3', '4', '5'];
const EXTRA_USER_INFO_FAMILY_STATUS_OPTIONS = ['1', '2'];
const EXTRA_USER_INFO_YES_NO_OPTIONS = ['true', 'false'];

function toFormString(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value);
}

function getDictionaryOption(value, options) {
  const normalizedValue = toFormString(value);

  if (!normalizedValue || !options.includes(normalizedValue)) {
    return '';
  }

  return normalizedValue;
}

function toYesNoOption(value) {
  if (value === true || value === 'true' || value === 't' || value === 1 || value === '1') {
    return 'true';
  }

  if (value === false || value === 'false' || value === 'f' || value === 0 || value === '0') {
    return 'false';
  }

  return '';
}

function parseYesNoOption(value) {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return null;
}

function getUnresolvedDictionaryLabel(rawValue, aliases, translate, dictionaryPath) {
  if (!isPresent(rawValue) || typeof rawValue === 'object') {
    return null;
  }

  const normalized = String(rawValue).trim();
  const code = aliases[normalized] || aliases[normalized.toLowerCase()] || normalized;
  const translated = translateCode(translate, dictionaryPath, code);

  return translated || normalized || null;
}

function getUnresolvedYesNoLabel(value, translate) {
  const option = toYesNoOption(value);

  if (!option) {
    return null;
  }

  return translate(`message.editWorkInfoPage.yesNo.list.${option}`);
}

export function isLegacyExtraUserInfoStructure(extraUserInfo) {
  if (!extraUserInfo || typeof extraUserInfo !== 'object') {
    return false;
  }

  return Boolean(
    extraUserInfo.number_of_dependents !== undefined
    || extraUserInfo.driving_license !== undefined
    || extraUserInfo.car_owned !== undefined
    || extraUserInfo.real_estate_owned !== undefined
    || extraUserInfo.need_to_refund_expenses !== undefined
    || extraUserInfo.monthly_income !== undefined
    || extraUserInfo.monthly_income_minor !== undefined
  );
}

export function getExtraUserInfoEducationOptions() {
  return [...EXTRA_USER_INFO_EDUCATION_OPTIONS];
}

export function getExtraUserInfoFamilyStatusOptions() {
  return [...EXTRA_USER_INFO_FAMILY_STATUS_OPTIONS];
}

export function getExtraUserInfoYesNoOptions() {
  return [...EXTRA_USER_INFO_YES_NO_OPTIONS];
}

export function buildExtraUserInfoEditForm(extraUserInfo, translate) {
  const emptyUnresolved = {
    education: null,
    drivers_license: null,
    car_owner: null,
    real_estate_owner: null,
    family_status: null,
    dependens_number: null,
    child_expenses: null,
    total_income: null
  };

  const emptyForm = {
    education: '',
    drivers_license: '',
    car_owner: '',
    real_estate_owner: '',
    family_status: '',
    dependens_number: '',
    child_expenses: '',
    total_income: '',
    unresolved: emptyUnresolved
  };

  if (!extraUserInfo || typeof extraUserInfo !== 'object') {
    return emptyForm;
  }

  if (isLegacyExtraUserInfoStructure(extraUserInfo)) {
    return {
      education: '',
      drivers_license: '',
      car_owner: '',
      real_estate_owner: '',
      family_status: '',
      dependens_number: '',
      child_expenses: '',
      total_income: '',
      unresolved: {
        education: getUnresolvedDictionaryLabel(
          extraUserInfo.education,
          EDUCATION_ALIASES,
          translate,
          'message.signUp.extraBlock.education.list.'
        ),
        drivers_license: getUnresolvedYesNoLabel(
          extraUserInfo.driving_license ?? extraUserInfo.drivers_license,
          translate
        ),
        car_owner: getUnresolvedYesNoLabel(
          extraUserInfo.car_owned ?? extraUserInfo.car_owner,
          translate
        ),
        real_estate_owner: getUnresolvedYesNoLabel(
          extraUserInfo.real_estate_owned ?? extraUserInfo.real_estate_owner,
          translate
        ),
        family_status: getUnresolvedDictionaryLabel(
          extraUserInfo.family_status,
          FAMILY_STATUS_ALIASES,
          translate,
          'message.signUp.extraBlock.familyStatus.list.'
        ),
        dependens_number: toFormString(
          extraUserInfo.number_of_dependents ?? extraUserInfo.dependens_number
        ) || null,
        child_expenses: getUnresolvedYesNoLabel(
          extraUserInfo.need_to_refund_expenses ?? extraUserInfo.child_expenses,
          translate
        ),
        total_income: toFormString(
          extraUserInfo.monthly_income ?? extraUserInfo.total_income
        ) || null
      }
    };
  }

  return {
    education: getDictionaryOption(extraUserInfo.education, EXTRA_USER_INFO_EDUCATION_OPTIONS),
    drivers_license: toYesNoOption(
      extraUserInfo.drivers_license ?? extraUserInfo.driving_license
    ),
    car_owner: toYesNoOption(extraUserInfo.car_owner ?? extraUserInfo.car_owned),
    real_estate_owner: toYesNoOption(
      extraUserInfo.real_estate_owner ?? extraUserInfo.real_estate_owned
    ),
    family_status: getDictionaryOption(
      extraUserInfo.family_status,
      EXTRA_USER_INFO_FAMILY_STATUS_OPTIONS
    ),
    dependens_number: toFormString(
      extraUserInfo.dependens_number ?? extraUserInfo.number_of_dependents
    ),
    child_expenses: toYesNoOption(
      extraUserInfo.child_expenses ?? extraUserInfo.need_to_refund_expenses
    ),
    total_income: toFormString(
      extraUserInfo.total_income ?? extraUserInfo.monthly_income
    ),
    unresolved: emptyUnresolved
  };
}

export function buildExtraUserInfoSavePayload(form) {
  const payload = {};

  if (!form || typeof form !== 'object') {
    return payload;
  }

  const education = toFormString(form.education).trim();
  const driversLicense = parseYesNoOption(form.drivers_license);
  const carOwner = parseYesNoOption(form.car_owner);
  const realEstateOwner = parseYesNoOption(form.real_estate_owner);
  const familyStatus = toFormString(form.family_status).trim();
  const dependensNumber = toFormString(form.dependens_number).trim();
  const childExpenses = parseYesNoOption(form.child_expenses);
  const totalIncome = toFormString(form.total_income).trim();

  if (education) {
    payload.education = education;
  }

  if (driversLicense !== null) {
    payload.drivers_license = driversLicense;
  }

  if (carOwner !== null) {
    payload.car_owner = carOwner;
  }

  if (realEstateOwner !== null) {
    payload.real_estate_owner = realEstateOwner;
  }

  if (familyStatus) {
    payload.family_status = familyStatus;
  }

  if (dependensNumber !== '') {
    payload.dependens_number = dependensNumber;
  }

  if (childExpenses !== null) {
    payload.child_expenses = childExpenses;
  }

  if (totalIncome) {
    payload.total_income = totalIncome;
  }

  return payload;
}
